import React, { useState } from 'react';
import moment from 'moment/moment';
import { SelectOption } from 'global';
import { useQuery } from '@apollo/client';
import { blue } from '@mui/material/colors';
import { useForm, useWatch } from 'react-hook-form';
import { AutocompleteField, DateField } from 'components/form';
import { Box, Button, Chip, FormControl, Stack, Typography } from '@mui/material';
import LocationsTreeViewDropdown from 'components/LocationsTreeViewDropdown/LocationsTreeViewDropdown';
import { MetricsVariables } from './DashboardPage';
import { dateRangeConfiguration, Q_LOCATIONS } from './index';
import { buildTree, getDropdownTreeSelectDefaultOptions } from '../../../../services/locationsService';

interface Props {
  contactsOptions: SelectOption[];
  onFetchMetrics: (variables: MetricsVariables['input']) => Promise<void>;
}

const initialFilterValue = {
  locationId: null,
  userProfileId: null,
  to: moment().endOf('month'),
  from: moment().startOf('month'),
};

export const DashboardFilters = ({ onFetchMetrics, contactsOptions = [] }: Props) => {
  const { control, getValues, setValue, reset } = useForm({
    defaultValues: initialFilterValue,
  });

  const currentTo = useWatch({ control, name: 'to' });
  const currentFrom = useWatch({ control, name: 'from' });
  const locationId = useWatch({ control, name: 'locationId' });
  const userProfileId = useWatch({ control, name: 'userProfileId' });

  const [locationsTree, setLocationsTree] = useState<any[]>();

  const { data: locationsDataResponse } = useQuery(Q_LOCATIONS, {
    onCompleted(locationsData) {
      if (!Array.isArray(locationsData.locations)) return;

      const options = locationsData.locations.map((location: any) => ({
        ...location,
        checked: location.locationId === locationId,
      }));

      options.push({ locationId: null, locationName: 'Unassigned', checked: locationId == null });

      setLocationsTree(buildTree(options));
    },
  });

  const treeSelectOptions = getDropdownTreeSelectDefaultOptions({
    onChange: async (selectedNode: any) => {
      const options = (locationsDataResponse?.locations ?? []).map((location: any) => ({
        ...location,
        checked: location.locationId === selectedNode.value,
      }));

      options.push({ locationId: null, locationName: 'Unassigned', checked: selectedNode.value == null });

      setLocationsTree(buildTree(options));

      setValue('locationId', selectedNode.value);
      await onFetchMetrics({
        locationId: selectedNode.value,
        to: getValues('to').format('YYYY-MM-DD'),
        from: getValues('from').format('YYYY-MM-DD'),
      });
    },
    texts: {
      placeholder: 'Choose Location',
    },
  });

  const handleSelectRange = async (range: { endDate: moment.Moment; startDate: moment.Moment }) => {
    setValue('to', range.endDate);
    setValue('from', range.startDate);

    await onFetchMetrics({
      ...getValues(),
      to: range.endDate.format('YYYY-MM-DD'),
      from: range.startDate.format('YYYY-MM-DD'),
    });
  };

  const handleResetFilters = async () => {
    reset({ ...initialFilterValue, locationId: undefined, userProfileId: undefined }, { keepDefaultValues: false });

    const options = [
      ...(locationsDataResponse?.locations ?? []).map((location: any) => ({
        ...location,
        checked: false,
      })),
      { locationId: null, locationName: 'Unassigned', checked: true },
    ];
    setLocationsTree(buildTree(options));

    await onFetchMetrics({
      ...initialFilterValue,
      to: moment().format('YYYY-MM-DD'),
      from: moment().subtract(1, 'year').format('YYYY-MM-DD'),
    });
  };

  return (
    <Stack>
      <Stack direction="row" justifyContent="space-between">
        <Typography variant="h6" component="span">
          Filters:
        </Typography>

        <Button variant="contained" onClick={handleResetFilters}>
          Reset
        </Button>
      </Stack>

      <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '1rem' }}>
        <Box sx={{ display: 'grid', gridTemplateRows: 'auto auto', gap: '1rem' }}>
          <Stack direction="row" gap="1rem">
            <DateField
              name="from"
              label="From"
              control={control}
              extraAction={async nextValue => {
                if (nextValue) {
                  await onFetchMetrics({
                    from: nextValue.format('YYYY-MM-DD'),
                    to: getValues('to').format('YYYY-MM-DD'),
                  });
                }
              }}
            />
            <DateField
              name="to"
              label="To"
              control={control}
              maxDate={moment().endOf('year')}
              extraAction={async nextValue => {
                if (nextValue) {
                  await onFetchMetrics({
                    from: getValues('from').format('YYYY-MM-DD'),
                    to: nextValue.format('YYYY-MM-DD'),
                  });
                }
              }}
            />
          </Stack>

          <Stack direction="row" gap="0.5rem" flexWrap="wrap">
            {dateRangeConfiguration.map(({ label, value, range }) => {
              const isSelectedRange =
                currentTo.isSame(range.endDate, 'day') && currentFrom.isSame(range.startDate, 'day');

              return (
                <Chip
                  key={value}
                  color={isSelectedRange ? 'primary' : 'default'}
                  variant={isSelectedRange ? 'filled' : 'outlined'}
                  label={<Typography variant="body2">{label}</Typography>}
                  sx={{
                    '&:hover': {
                      cursor: isSelectedRange ? 'not-allowed' : 'pointer',
                      ...(isSelectedRange && { backgroundColor: blue[600], boxShadow: 'none' }),
                    },
                  }}
                  onClick={async () => {
                    if (isSelectedRange) return;
                    await handleSelectRange(range);
                  }}
                />
              );
            })}
          </Stack>
        </Box>

        <Stack sx={{ paddingTop: '12px' }}>
          <FormControl fullWidth>
            <LocationsTreeViewDropdown tree={locationsTree ?? []} treeSelectOptions={treeSelectOptions} />
          </FormControl>
        </Stack>

        <AutocompleteField
          control={control}
          name="userProfileId"
          options={contactsOptions}
          key={userProfileId ?? 'empty'}
          textFieldProps={{ variant: 'standard', label: 'Created By' }}
          extraAction={async nextValue => {
            await onFetchMetrics({
              userProfileId: nextValue?.value ?? null,
              to: getValues('to').format('YYYY-MM-DD'),
              from: getValues('from').format('YYYY-MM-DD'),
            });
          }}
        />
      </Box>
    </Stack>
  );
};
